/**
 * Router root.
 *
 * Structure:
 *   /login                 — public
 *   /                      — protected, renders AdminLayout
 *     /dashboard           — implemented
 *     /{navItem}           — placeholder for items not yet built (campaigns,
 *                            products, prompts, templates, followups, …)
 *   *                      — catches unknown paths → redirect /dashboard
 *
 * Providers (ThemeProvider, Toaster, GlobalProgressBar) are mounted here so
 * both public and protected routes get them.
 */

import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { GlobalProgressBar } from '@/components/GlobalProgressBar';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ThemeProvider } from '@/components/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AdminUsersPage } from '@/pages/AdminUsers';
import { AuditLogPage } from '@/pages/AuditLog';
import { CallbacksPage } from '@/pages/Callbacks';
import { CallbackDetailPage } from '@/pages/CallbackDetail';
import { CampaignsPage } from '@/pages/Campaigns';
import { CampaignDetailPage } from '@/pages/CampaignDetail';
import { CampaignFollowupPage } from '@/pages/CampaignFollowup';
import { CampaignVariantsPage } from '@/pages/CampaignVariants';
import { CampaignVariantMetricsPage } from '@/pages/CampaignVariantMetrics';
import { CostDashboardPage } from '@/pages/CostDashboard';
import { DashboardPage } from '@/pages/Dashboard';
import { FollowupsPage } from '@/pages/Followups';
import { FollowupDetailPage } from '@/pages/FollowupDetail';
import { LoginPage } from '@/pages/Login';
import { ConversationsPage } from '@/pages/Conversations';
import { ConversationDetailPage } from '@/pages/ConversationDetail';
import { OptOutKeywordsPage } from '@/pages/OptOutKeywords';
import { ProductsPage } from '@/pages/Products';
import { PromptsPage } from '@/pages/Prompts';
import { PromptAssistantPage } from '@/pages/PromptAssistant';
import { PromptAssistantHistoryPage } from '@/pages/PromptAssistantHistory';
import { PromptDetailPage } from '@/pages/PromptDetail';
import { SettingsPage } from '@/pages/Settings';
import { TemplatesPage } from '@/pages/Templates';
import { TestNumbersPage } from '@/pages/TestNumbers';

export default function App() {
  return (
    <ThemeProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <GlobalProgressBar />
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/campaigns" element={<CampaignsPage />} />
              <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
              <Route path="/campaigns/:id/followup" element={<CampaignFollowupPage />} />
              <Route path="/campaigns/:id/variants" element={<CampaignVariantsPage />} />
              <Route path="/campaigns/:id/metrics" element={<CampaignVariantMetricsPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/prompts" element={<PromptsPage />} />
              <Route path="/prompts/:id" element={<PromptDetailPage />} />
              <Route path="/prompts/:id/assistant" element={<PromptAssistantPage />} />
              <Route
                path="/prompts/:id/assistant/history"
                element={<PromptAssistantHistoryPage />}
              />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/followups" element={<FollowupsPage />} />
              <Route path="/followups/:id" element={<FollowupDetailPage />} />
              <Route path="/opt-out-keywords" element={<OptOutKeywordsPage />} />
              <Route path="/test-numbers" element={<TestNumbersPage />} />
              <Route path="/conversations" element={<ConversationsPage />} />
              <Route path="/conversations/:id" element={<ConversationDetailPage />} />
              <Route path="/callbacks" element={<CallbacksPage />} />
              <Route path="/callbacks/:id" element={<CallbackDetailPage />} />
              <Route path="/cost-dashboard" element={<CostDashboardPage />} />
              <Route path="/users" element={<AdminUsersPage />} />
              <Route path="/audit-log" element={<AuditLogPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster />
      </Router>
    </ThemeProvider>
  );
}
